import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, Lock, Eye, EyeOff, AlertCircle } from "lucide-react";
import {
  PHONE_LENGTH,
  sanitizePhone,
  validatePhoneNumber,
  validatePassword,
  getPasswordStrength
} from "../utils/validation";

const API_URL = process.env.REACT_APP_API_URL || "https://api.ithalbymr.store/api";

const ForgotPassword = () => {
  const [phone, setPhone] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [touched, setTouched] = useState({ phone: false, password: false });

  const navigate = useNavigate();

  // Real-time validation
  const phoneError = validatePhoneNumber(phone);
  const passwordError = validatePassword(newPassword);
  const passwordStrength = useMemo(() => getPasswordStrength(newPassword), [newPassword]);

  const isSubmitDisabled = !!phoneError || !!passwordError || !phone || !newPassword;

  const handlePhoneChange = (e) => {
    const value = sanitizePhone(e.target.value);
    if (value.length > PHONE_LENGTH) return;
    setPhone(value);
    setTouched(prev => ({ ...prev, phone: true }));
    setErrorMessage("");
    setSuccessMessage("");
  };

  const handlePasswordChange = (e) => {
    setNewPassword(e.target.value);
    setTouched(prev => ({ ...prev, password: true }));
    setErrorMessage("");
    setSuccessMessage("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ phone: true, password: true });

    if (isSubmitDisabled) return;

    try {
      const res = await fetch(`${API_URL}/users/reset-password-direct`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone, password: newPassword })
      });

      const data = await res.json();
      
      if (res.ok) {
        setSuccessMessage(data.message || "Password updated successfully");
      } else {
        setErrorMessage(data.message || "Failed to update password");
      }
    } catch (error) {
      setErrorMessage("Something went wrong. Please try again.");
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">

        <div className="welcome-section">
          <h2 className="welcome-heading">Reset Password</h2>
          <p className="welcome-subtitle">
           <span style={{ color: "#db2777" }}> Enter your phone number and new password</span>
          </p>
        </div>

        <form className="login-form" autoComplete="off" onSubmit={handleSubmit}>

          {/* Phone Number Field */}
          <div className="form-group">
            <label>Phone Number</label>
            <div className="input-wrapper">
              <Phone size={20} className="input-icon" />
              <input
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                name="phone"
                autoComplete="new-phone"
                placeholder="Enter phone number"
                value={phone}
                onChange={handlePhoneChange}
                maxLength={PHONE_LENGTH}
                required
              />
            </div>
            {touched.phone && phoneError && (
              <p className="error-text">{phoneError}</p>
            )}
          </div>

          {/* New Password Field */}
          <div className="form-group">
            <label>New Password</label>
            <div className="input-wrapper">
              <Lock size={20} className="input-icon" />
              <input
                type={showPassword ? "text" : "password"}
                name="new-password"
                autoComplete="new-password"
                placeholder="Enter new password"
                value={newPassword}
                onChange={handlePasswordChange}
                className="password-input"
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            {/* Password strength checklist */}
            {newPassword && (
              <div className="password-review">
                <p className="review-title">Password Requirements:</p>
                <div className="review-items">
                  <div className={`review-item ${passwordStrength.hasMinLength ? "valid" : "invalid"}`}>
                    <span className="review-icon">{passwordStrength.hasMinLength ? "✅" : "❌"}</span>
                    <span>8+ characters</span>
                  </div>
                  <div className={`review-item ${passwordStrength.hasUppercase ? "valid" : "invalid"}`}>
                    <span className="review-icon">{passwordStrength.hasUppercase ? "✅" : "❌"}</span>
                    <span>1 uppercase letter</span>
                  </div>
                  <div className={`review-item ${passwordStrength.hasLowercase ? "valid" : "invalid"}`}>
                    <span className="review-icon">{passwordStrength.hasLowercase ? "✅" : "❌"}</span>
                    <span>1 lowercase letter</span>
                  </div>
                  <div className={`review-item ${passwordStrength.hasNumber ? "valid" : "invalid"}`}>
                    <span className="review-icon">{passwordStrength.hasNumber ? "✅" : "❌"}</span>
                    <span>1 number</span>
                  </div>
                  <div className={`review-item ${passwordStrength.hasSpecialChar ? "valid" : "invalid"}`}>
                    <span className="review-icon">{passwordStrength.hasSpecialChar ? "✅" : "❌"}</span>
                    <span>1 special character (@#$!%*?&)</span>
                  </div>
                </div>
              </div>
            )}

            {touched.password && passwordError && (
              <p className="error-text">{passwordError}</p>
            )}
          </div>

          {/* General message (success or error from API) */}
          {errorMessage && (
            <div className="error-message fade-in">
              <AlertCircle size={16} />
              <span>{errorMessage}</span>
            </div>
          )}
          {successMessage && (
            <div className="success-message fade-in">
              <span style={{ fontSize: '16px' }}>✓</span>
              <span>{successMessage}</span>
            </div>
          )}

          <button
            type="submit"
            className={`btn-primary ${isSubmitDisabled ? "disabled" : ""}`}
            disabled={isSubmitDisabled}
          >
            Update Password
          </button>
        </form>

        {/* Back to Login - separated with spacing */}
        <p
          className="forgot-password-text"
          style={{ marginTop: "20px" }}
          onClick={() => navigate("/login")}
        >
          ← Back to Login
        </p>

      </div>
    </div>
  );
};

export default ForgotPassword;
